import React from "react";
import { SettingGroup, SettingField, SettingToggle, NumberInput, ExampleBlock } from "./SettingField";
import { SubCabinetFillDiagram } from "./SettingsDiagrams";

// Must match the C# defaults in PlacementOptions.cs. Scenarios that never
// saved Space Oriented options have placementOptions = null, and the backend
// then uses these values - so the form has to show them too.
export const SPACE_DEFAULTS = {
  enableSubCabinetFill: true,
  bandSeparatorCableDuctWidthMm: 40,
  requireCurrentRowGEQNextFirstRow: false,
  emitBandSeparatorCableDuct: true,
};

export const withSpaceDefaults = (opts) => ({ ...SPACE_DEFAULTS, ...(opts ?? {}) });

/**
 * Space Oriented options (Settings.PlacementOptions).
 *  placementOptions / onChange(field, value)
 *  fillMountingSpace: the general "Fill free space from" value, used in the texts
 */
const SpaceOptionsInputs = ({ placementOptions, onChange, fillMountingSpace = 0 }) => {
  const opts = withSpaceDefaults(placementOptions);
  const fillOff = !opts.enableSubCabinetFill;

  return (
    <div className="ssp-space-options">
      <SettingGroup
        title="Filling free space"
        intro="Space Oriented keeps sections in rows, but can use the free end of a row for the next sections."
      >
        <SettingToggle
          id="ssp-subcabinet"
          label="Fill the end of rows with the next sections"
          checked={opts.enableSubCabinetFill}
          onChange={(v) => onChange("enableSubCabinetFill", v)}
          hint="When a row has space left, the following sections are placed there instead of on a new row."
          example={
            <ExampleBlock diagram={<SubCabinetFillDiagram />}>
              Protection devices fill 60&nbsp;% of their last row. <b>On:</b> the next section
              (e.g. relays) is placed in the remaining 40&nbsp;%, behind a vertical separator
              duct. <b>Off:</b> it starts on a new row. Only gaps of at least{" "}
              {fillMountingSpace}&nbsp;mm are used (&ldquo;Fill free space from&rdquo;).
            </ExampleBlock>
          }
        />

        <SettingToggle
          id="ssp-row-gate"
          label="Only fill with sections that fit the row height"
          checked={opts.requireCurrentRowGEQNextFirstRow}
          disabled={fillOff}
          onChange={(v) => onChange("requireCurrentRowGEQNextFirstRow", v)}
          hint={
            fillOff
              ? "Needs \u201CFill the end of rows\u201D."
              : "Skip a following section whose first devices are taller than the current row."
          }
          example={
            <ExampleBlock>
              A row of 90&nbsp;mm breakers has space left, and the next section starts with
              150&nbsp;mm contactors. <b>On:</b> the contactors start their own row; the gap
              stays empty. <b>Off:</b> the engine still tries &ndash; whatever fits in height
              goes into the gap, the rest moves to a new row.
            </ExampleBlock>
          }
        />
      </SettingGroup>

      <SettingGroup title="Separator duct">
        <SettingToggle
          id="ssp-emit-separator"
          label="Draw separator duct"
          checked={opts.emitBandSeparatorCableDuct}
          disabled={fillOff}
          onChange={(v) => onChange("emitBandSeparatorCableDuct", v)}
          hint="The vertical duct between a row and the section filled in after it. The space is reserved either way."
        />

        <SettingField
          id="ssp-separator-width"
          label="Separator duct width"
          disabled={fillOff}
          hint="Width of that vertical duct."
        >
          <NumberInput
            id="ssp-separator-width"
            value={opts.bandSeparatorCableDuctWidthMm}
            min={0}
            unit="mm"
            disabled={fillOff}
            onChange={(v) => onChange("bandSeparatorCableDuctWidthMm", v)}
          />
        </SettingField>
      </SettingGroup>
    </div>
  );
};

export default SpaceOptionsInputs;
